import { api } from 'sharedKernel/api/config'
import { getPathExtension } from './path'

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  tiff: 'image/tiff',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mp3: 'audio/mpeg',
  txt: 'text/plain',
  csv: 'text/csv',
  xml: 'application/xml',
  zip: 'application/zip',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  odt: 'application/vnd.oasis.opendocument.text',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  odp: 'application/vnd.oasis.opendocument.presentation'
}

const FALLBACK_MIME_TYPE = 'application/octet-stream'

const GENERIC_MIME_TYPES = new Set(['', 'text/plain', 'binary/octet-stream', FALLBACK_MIME_TYPE])

const CLOSED_TAB_CHECK_INTERVAL_IN_MS = 5000

interface RemoteFile {
  url: string
  fallbackPath?: string
  name?: string
  mimetype?: string
}

const isSpecific = (mimeType: string): boolean =>
  mimeType.trim() !== '' && !GENERIC_MIME_TYPES.has(mimeType.trim().toLowerCase())

export const resolveFileMimeType = ({ name, mimetype, fallbackPath }: RemoteFile): string => {
  const declared = mimetype?.trim().toLowerCase() ?? ''
  if (isSpecific(declared)) return declared

  const extension = getPathExtension(name) || getPathExtension(fallbackPath)
  const fromExtension = MIME_BY_EXTENSION[extension]
  if (fromExtension !== undefined) return fromExtension

  return declared !== '' ? declared : FALLBACK_MIME_TYPE
}

const releaseBlobUrlWhenTabCloses = (blobUrl: string, tab: Window | null): void => {
  if (tab === null) return

  const interval = setInterval(() => {
    if (!tab.closed) return

    clearInterval(interval)
    URL.revokeObjectURL(blobUrl)
  }, CLOSED_TAB_CHECK_INTERVAL_IN_MS)
}

export const openRemoteFileInNewTab = async (file: RemoteFile): Promise<void> => {
  const newTab = window.open('', '_blank')

  const openDirectly = (): void => {
    if (file.fallbackPath == null) {
      newTab?.close()
      return
    }
    if (newTab !== null) {
      newTab.location.href = file.fallbackPath
      return
    }
    window.open(file.fallbackPath, '_blank')
  }

  try {
    const { data } = await api.get<Blob>(file.url, { responseType: 'blob' })

    const mimeType = isSpecific(data.type) ? data.type : resolveFileMimeType(file)
    const typedBlob = data.type === mimeType ? data : new Blob([data], { type: mimeType })
    const blobUrl = URL.createObjectURL(typedBlob)

    if (newTab !== null) {
      newTab.location.href = blobUrl
    } else {
      window.open(blobUrl, '_blank')
    }

    releaseBlobUrlWhenTabCloses(blobUrl, newTab)
  } catch {
    openDirectly()
  }
}
